import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { formatDate } from "../lib/format.js";
import { PageSpinner, EmptyState } from "../components/ui.js";
import { IconTimeline } from "../components/icons.js";

interface Event {
  id: string;
  date: string;
  category: string;
  title: string;
  detail: string | null;
}

const FILTERS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "weight", label: "Weight" },
  { key: "strength", label: "Strength" },
  { key: "body", label: "Body" },
  { key: "recovery", label: "Recovery" },
  { key: "goal", label: "Goals" },
  { key: "phase", label: "Phases" }
];

const DOT: Record<string, string> = {
  weight: "var(--accent)",
  strength: "var(--warning, #e0a43b)",
  body: "var(--accent)",
  recovery: "var(--success)",
  phase: "var(--text-tertiary)",
  goal: "var(--success)",
  nutrition: "var(--accent)"
};

export function TimelinePage() {
  const [events, setEvents] = useState<Event[] | null>(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    try { setEvents((await api.get<{ events: Event[] }>(`/api/timeline${filter ? `?category=${filter}` : ""}`)).events); }
    catch { setEvents([]); }
  }, [filter]);
  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      <div className="page-header">
        <h1>Timeline</h1>
        <p className="subtitle">The story of your progress — the moments that mattered.</p>
      </div>

      <div className="chip-row" style={{ marginBottom: "var(--space-4)" }}>
        {FILTERS.map((f) => (
          <button key={f.key} type="button" className={`chip${filter === f.key ? " active" : ""}`} onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      {!events ? <PageSpinner /> : events.length === 0 ? (
        <EmptyState icon={<IconTimeline />} title="Your story starts here">
          As you hit milestones, set PRs, reach goals, and move through phases, they'll appear here as a timeline — not a log dump, just the moments worth remembering.
        </EmptyState>
      ) : (
        <div className="timeline">
          {events.map((e) => (
            <div className="timeline-item" key={e.id}>
              <div className="timeline-dot" style={{ background: DOT[e.category] ?? "var(--accent)" }} />
              <div className="timeline-body">
                <div className="dim text-xs">{formatDate(e.date)}</div>
                <div style={{ fontWeight: 600 }}>{e.title}</div>
                {e.detail && <div className="muted text-sm">{e.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
